import React, { useState, useRef, useEffect } from 'react';
import { Card } from '../../ui/Card';
import { Button } from '../../ui/Button';
import { Input } from '../../ui/Input';
import { Select } from '../../ui/Select';
import { Badge } from '../../ui/Badge';
import { colors, agentThemes, spacing, borderRadius, shadows } from '../../../constants/theme';

interface Creative {
  id: string;
  type: 'poster' | 'banner' | 'flyer' | 'pop' | 'digital';
  name: string;
  template: string;
  dimensions: {
    width: number;
    height: number;
    unit: 'px' | 'mm' | 'cm';
  };
  elements: CreativeElement[];
  background: {
    type: 'color' | 'gradient' | 'image';
    value: string;
  };
  metadata: {
    createdAt: Date;
    updatedAt: Date;
    author: string;
    campaign?: string;
  };
}

interface CreativeElement {
  id: string;
  type: 'text' | 'image' | 'shape' | 'qrcode';
  content: string;
  position: { x: number; y: number };
  size: { width: number; height: number };
  style: {
    color?: string;
    fontSize?: number;
    fontFamily?: string;
    fontWeight?: number;
    backgroundColor?: string;
    borderRadius?: number;
    opacity?: number;
    rotation?: number;
  };
  locked?: boolean;
  visible?: boolean;
}

interface Template {
  id: string;
  name: string;
  category: string;
  thumbnail: string;
  dimensions: Creative['dimensions'];
  elements: CreativeElement[];
}

interface CreativeEditorProps {
  creativeId?: string;
  templateId?: string;
  onSave?: (creative: Creative) => void;
  onExport?: (creative: Creative, format: 'pdf' | 'png' | 'jpg') => void;
}

const CreativeEditor: React.FC<CreativeEditorProps> = ({
  creativeId,
  templateId,
  onSave,
  onExport
}) => {
  const [creative, setCreative] = useState<Creative | null>(null);
  const [selectedElement, setSelectedElement] = useState<CreativeElement | null>(null);
  const [editMode, setEditMode] = useState<'select' | 'text' | 'shape' | 'image'>('select');
  const [showLayers, setShowLayers] = useState(true);
  const [showRuler, setShowRuler] = useState(true);
  const [zoom, setZoom] = useState(100);
  const canvasRef = useRef<HTMLDivElement>(null);
  const [history, setHistory] = useState<Creative[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);

  // テンプレートサンプル
  const templates: Template[] = [
    {
      id: 'temp001',
      name: '春のセールポスター',
      category: 'seasonal',
      thumbnail: '🌸',
      dimensions: { width: 420, height: 594, unit: 'mm' },
      elements: [
        {
          id: 'el1',
          type: 'text',
          content: '春の大感謝祭',
          position: { x: 50, y: 50 },
          size: { width: 320, height: 80 },
          style: {
            fontSize: 48,
            fontWeight: 700,
            color: '#E85C56',
            fontFamily: 'Noto Sans JP'
          },
          locked: false,
          visible: true
        },
        {
          id: 'el2',
          type: 'text',
          content: '全品最大50%OFF',
          position: { x: 50, y: 150 },
          size: { width: 320, height: 60 },
          style: {
            fontSize: 36,
            fontWeight: 500,
            color: '#2D3138',
            fontFamily: 'Noto Sans JP'
          },
          locked: false,
          visible: true
        },
        {
          id: 'el3',
          type: 'shape',
          content: 'rectangle',
          position: { x: 30, y: 250 },
          size: { width: 360, height: 200 },
          style: {
            backgroundColor: '#FFE5E3',
            borderRadius: 16,
            opacity: 0.8
          },
          locked: false,
          visible: true
        }
      ]
    }
  ];

  // 初期化
  useEffect(() => {
    const initialCreative: Creative = {
      id: creativeId || `creative-${Date.now()}`,
      type: 'poster',
      name: '新規クリエイティブ',
      template: templateId || 'temp001',
      dimensions: { width: 420, height: 594, unit: 'mm' },
      elements: templates[0].elements,
      background: {
        type: 'gradient',
        value: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      },
      metadata: {
        createdAt: new Date(),
        updatedAt: new Date(),
        author: 'ユーザー',
        campaign: 'Spring Sale 2025'
      }
    };
    setCreative(initialCreative);
    setHistory([initialCreative]);
    setHistoryIndex(0);
  }, [creativeId, templateId]);

  // 履歴管理
  const addToHistory = (newCreative: Creative) => {
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(newCreative);
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  };

  const undo = () => {
    if (historyIndex > 0) {
      setHistoryIndex(historyIndex - 1);
      setCreative(history[historyIndex - 1]);
    }
  };

  const redo = () => {
    if (historyIndex < history.length - 1) {
      setHistoryIndex(historyIndex + 1);
      setCreative(history[historyIndex + 1]);
    }
  };

  // 要素の更新
  const updateElement = (elementId: string, updates: Partial<CreativeElement>) => {
    if (!creative) return;
    
    const newCreative = {
      ...creative,
      elements: creative.elements.map(el => 
        el.id === elementId ? { ...el, ...updates } : el
      ),
      metadata: {
        ...creative.metadata,
        updatedAt: new Date()
      }
    };
    setCreative(newCreative);
    addToHistory(newCreative);
  };

  // 要素の追加
  const addElement = (type: CreativeElement['type']) => {
    if (!creative) return;
    
    const newElement: CreativeElement = {
      id: `el-${Date.now()}`,
      type,
      content: type === 'text' ? '新規テキスト' : type,
      position: { x: 100, y: 100 },
      size: { width: 200, height: type === 'text' ? 50 : 100 },
      style: {
        color: '#2D3138',
        fontSize: 24,
        fontFamily: 'Noto Sans JP',
        fontWeight: 400
      },
      locked: false,
      visible: true
    };

    const newCreative = {
      ...creative,
      elements: [...creative.elements, newElement],
      metadata: {
        ...creative.metadata,
        updatedAt: new Date()
      }
    };
    setCreative(newCreative);
    addToHistory(newCreative);
    setSelectedElement(newElement);
  };

  // 要素の削除
  const deleteElement = (elementId: string) => {
    if (!creative) return;
    
    const newCreative = {
      ...creative,
      elements: creative.elements.filter(el => el.id !== elementId),
      metadata: {
        ...creative.metadata,
        updatedAt: new Date()
      }
    };
    setCreative(newCreative);
    addToHistory(newCreative);
    setSelectedElement(null);
  };

  // キャンバスのレンダリング
  const renderCanvas = () => {
    if (!creative) return null;

    const scale = zoom / 100;
    const canvasWidth = creative.dimensions.width;
    const canvasHeight = creative.dimensions.height;

    return (
      <div
        ref={canvasRef}
        style={{
          position: 'relative',
          width: canvasWidth * scale,
          height: canvasHeight * scale,
          background: creative.background.type === 'color' 
            ? creative.background.value 
            : creative.background.value,
          borderRadius: borderRadius.md,
          boxShadow: shadows.lg,
          overflow: 'hidden',
          transform: `scale(${scale})`,
          transformOrigin: 'top left'
        }}
      >
        {creative.elements
          .filter(el => el.visible)
          .map(element => (
            <div
              key={element.id}
              onClick={() => setSelectedElement(element)}
              style={{
                position: 'absolute',
                left: element.position.x,
                top: element.position.y,
                width: element.size.width,
                height: element.size.height,
                cursor: element.locked ? 'default' : 'move',
                border: selectedElement?.id === element.id 
                  ? `2px solid ${colors.accent.blue}` 
                  : 'none',
                padding: element.type === 'text' ? '8px' : '0',
                transform: `rotate(${element.style.rotation || 0}deg)`,
                opacity: element.style.opacity || 1,
                ...element.style,
                backgroundColor: element.type === 'shape' ? element.style.backgroundColor : 'transparent'
              }}
            >
              {element.type === 'text' && (
                <div
                  contentEditable={!element.locked && selectedElement?.id === element.id}
                  suppressContentEditableWarning
                  onBlur={(e) => {
                    updateElement(element.id, { content: e.currentTarget.textContent || '' });
                  }}
                  style={{
                    width: '100%',
                    height: '100%',
                    fontSize: element.style.fontSize,
                    fontFamily: element.style.fontFamily,
                    fontWeight: element.style.fontWeight,
                    color: element.style.color,
                    outline: 'none'
                  }}
                >
                  {element.content}
                </div>
              )}
              {element.type === 'shape' && (
                <div
                  style={{
                    width: '100%',
                    height: '100%',
                    backgroundColor: element.style.backgroundColor,
                    borderRadius: element.style.borderRadius
                  }}
                />
              )}
              {element.type === 'qrcode' && (
                <div style={{
                  width: '100%',
                  height: '100%',
                  backgroundColor: '#000',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#FFF',
                  fontSize: '0.875rem'
                }}>
                  QRコード
                </div>
              )}
              
              {/* リサイズハンドル */}
              {selectedElement?.id === element.id && !element.locked && (
                <>
                  <div style={{
                    position: 'absolute',
                    right: -4,
                    bottom: -4,
                    width: 8,
                    height: 8,
                    backgroundColor: colors.accent.blue,
                    cursor: 'se-resize',
                    borderRadius: '50%'
                  }} />
                  <div style={{
                    position: 'absolute',
                    right: -4,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: 8,
                    height: 8,
                    backgroundColor: colors.accent.blue,
                    cursor: 'e-resize',
                    borderRadius: '50%'
                  }} />
                  <div style={{
                    position: 'absolute',
                    bottom: -4,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: 8,
                    height: 8,
                    backgroundColor: colors.accent.blue,
                    cursor: 's-resize',
                    borderRadius: '50%'
                  }} />
                </>
              )}
            </div>
          ))}
      </div>
    );
  };

  return (
    <div style={{ padding: spacing.lg, height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* ヘッダー */}
      <div style={{ marginBottom: spacing.lg }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          marginBottom: spacing.md
        }}>
          <h2 style={{ 
            fontSize: '1.5rem', 
            fontWeight: 600, 
            color: colors.text.dark,
            display: 'flex',
            alignItems: 'center',
            gap: spacing.sm
          }}>
            <span>{agentThemes.creative.icon}</span>
            クリエイティブエディター
          </h2>
          <div style={{ display: 'flex', gap: spacing.sm }}>
            <Button
              variant="outline"
              size="sm"
              onClick={undo}
              disabled={historyIndex <= 0}
            >
              ↶ 戻る
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={redo}
              disabled={historyIndex >= history.length - 1}
            >
              ↷ 進む
            </Button>
            <Select
              value={zoom}
              onChange={(e) => setZoom(parseInt(e.target.value))}
              style={{ width: '100px' }}
            >
              <option value="50">50%</option>
              <option value="75">75%</option>
              <option value="100">100%</option>
              <option value="125">125%</option>
              <option value="150">150%</option>
            </Select>
            <Button
              variant="outline"
              onClick={() => creative && onExport?.(creative, 'pdf')}
            >
              エクスポート
            </Button>
            <Button
              style={{
                backgroundColor: agentThemes.creative.primary,
                color: '#FFFFFF'
              }}
              onClick={() => creative && onSave?.(creative)}
            >
              保存
            </Button>
          </div>
        </div>
      </div>

      <div style={{ flex: 1, display: 'flex', gap: spacing.lg, overflow: 'hidden' }}>
        {/* ツールパネル */}
        <Card style={{ width: '80px' }}>
          <div style={{ padding: spacing.sm }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.sm }}>
              {[
                { mode: 'select' as const, icon: '↖️', label: '選択' },
                { mode: 'text' as const, icon: 'T', label: 'テキスト' },
                { mode: 'shape' as const, icon: '□', label: '図形' },
                { mode: 'image' as const, icon: '🖼️', label: '画像' }
              ].map(tool => (
                <button
                  key={tool.mode}
                  onClick={() => {
                    setEditMode(tool.mode);
                    if (tool.mode !== 'select') {
                      addElement(tool.mode === 'text' ? 'text' : tool.mode === 'shape' ? 'shape' : 'image');
                    }
                  }}
                  style={{
                    width: '100%',
                    aspectRatio: '1',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '2px',
                    backgroundColor: editMode === tool.mode 
                      ? agentThemes.creative.secondary 
                      : colors.background.primary,
                    border: `1px solid ${editMode === tool.mode 
                      ? agentThemes.creative.primary 
                      : colors.support.gray}`,
                    borderRadius: borderRadius.sm,
                    cursor: 'pointer',
                    transition: 'all 200ms ease'
                  }}
                >
                  <div style={{ fontSize: '1.5rem' }}>{tool.icon}</div>
                  <div style={{ fontSize: '0.6rem', color: colors.text.secondary }}>
                    {tool.label}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </Card>

        {/* メインキャンバス */}
        <div style={{ 
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: colors.background.secondary,
          borderRadius: borderRadius.md,
          overflow: 'auto'
        }}>
          {renderCanvas()}
        </div>

        {/* プロパティパネル */}
        <Card style={{ width: '300px' }}>
          <div style={{ padding: spacing.md }}>
            {selectedElement ? (
              <>
                <h3 style={{ 
                  fontSize: '1rem',
                  fontWeight: 600,
                  color: colors.text.dark,
                  marginBottom: spacing.md
                }}>
                  要素のプロパティ
                </h3>

                <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.md }}>
                  {/* 基本設定 */}
                  <div>
                    <label style={{ 
                      display: 'block',
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      color: colors.text.dark,
                      marginBottom: spacing.xs
                    }}>
                      タイプ
                    </label>
                    <Badge variant="secondary">
                      {selectedElement.type}
                    </Badge>
                  </div>

                  {/* テキスト設定 */}
                  {selectedElement.type === 'text' && (
                    <>
                      <div>
                        <label style={{ 
                          display: 'block',
                          fontSize: '0.875rem',
                          fontWeight: 500,
                          color: colors.text.dark,
                          marginBottom: spacing.xs
                        }}>
                          フォントサイズ
                        </label>
                        <Input
                          type="number"
                          value={selectedElement.style.fontSize || 24}
                          onChange={(e) => updateElement(selectedElement.id, {
                            style: { ...selectedElement.style, fontSize: parseInt(e.target.value) }
                          })}
                          style={{ width: '100%' }}
                        />
                      </div>
                      <div>
                        <label style={{ 
                          display: 'block',
                          fontSize: '0.875rem',
                          fontWeight: 500,
                          color: colors.text.dark,
                          marginBottom: spacing.xs
                        }}>
                          色
                        </label>
                        <Input
                          type="color"
                          value={selectedElement.style.color || '#000000'}
                          onChange={(e) => updateElement(selectedElement.id, {
                            style: { ...selectedElement.style, color: e.target.value }
                          })}
                          style={{ width: '100%', height: '40px' }}
                        />
                      </div>
                    </>
                  )}

                  {/* 図形設定 */}
                  {selectedElement.type === 'shape' && (
                    <>
                      <div>
                        <label style={{ 
                          display: 'block',
                          fontSize: '0.875rem',
                          fontWeight: 500,
                          color: colors.text.dark,
                          marginBottom: spacing.xs
                        }}>
                          背景色
                        </label>
                        <Input
                          type="color"
                          value={selectedElement.style.backgroundColor || '#000000'}
                          onChange={(e) => updateElement(selectedElement.id, {
                            style: { ...selectedElement.style, backgroundColor: e.target.value }
                          })}
                          style={{ width: '100%', height: '40px' }}
                        />
                      </div>
                      <div>
                        <label style={{ 
                          display: 'block',
                          fontSize: '0.875rem',
                          fontWeight: 500,
                          color: colors.text.dark,
                          marginBottom: spacing.xs
                        }}>
                          角丸
                        </label>
                        <Input
                          type="number"
                          value={selectedElement.style.borderRadius || 0}
                          onChange={(e) => updateElement(selectedElement.id, {
                            style: { ...selectedElement.style, borderRadius: parseInt(e.target.value) }
                          })}
                          style={{ width: '100%' }}
                        />
                      </div>
                    </>
                  )}

                  {/* 共通設定 */}
                  <div>
                    <label style={{ 
                      display: 'block',
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      color: colors.text.dark,
                      marginBottom: spacing.xs
                    }}>
                      透明度
                    </label>
                    <Input
                      type="range"
                      min="0"
                      max="1"
                      step="0.1"
                      value={selectedElement.style.opacity || 1}
                      onChange={(e) => updateElement(selectedElement.id, {
                        style: { ...selectedElement.style, opacity: parseFloat(e.target.value) }
                      })}
                      style={{ width: '100%' }}
                    />
                  </div>

                  {/* アクション */}
                  <div style={{ 
                    display: 'flex',
                    gap: spacing.sm,
                    paddingTop: spacing.md,
                    borderTop: `1px solid ${colors.support.gray}`
                  }}>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => updateElement(selectedElement.id, {
                        locked: !selectedElement.locked
                      })}
                    >
                      {selectedElement.locked ? '🔒 ロック解除' : '🔓 ロック'}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => deleteElement(selectedElement.id)}
                      style={{ color: colors.status.error }}
                    >
                      削除
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              <div style={{ 
                textAlign: 'center',
                padding: spacing.xl,
                color: colors.text.light
              }}>
                <div style={{ fontSize: '2rem', marginBottom: spacing.sm }}>🎨</div>
                <div style={{ fontSize: '0.875rem' }}>
                  要素を選択してプロパティを編集
                </div>
              </div>
            )}

            {/* レイヤーパネル */}
            <div style={{ 
              marginTop: spacing.xl,
              paddingTop: spacing.lg,
              borderTop: `1px solid ${colors.support.gray}`
            }}>
              <h3 style={{ 
                fontSize: '1rem',
                fontWeight: 600,
                color: colors.text.dark,
                marginBottom: spacing.md
              }}>
                レイヤー
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.xs }}>
                {creative?.elements.map((element, index) => (
                  <div
                    key={element.id}
                    onClick={() => setSelectedElement(element)}
                    style={{
                      padding: spacing.sm,
                      backgroundColor: selectedElement?.id === element.id 
                        ? agentThemes.creative.secondary 
                        : colors.background.primary,
                      border: `1px solid ${selectedElement?.id === element.id 
                        ? agentThemes.creative.primary 
                        : colors.support.gray}`,
                      borderRadius: borderRadius.sm,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: spacing.xs }}>
                      <span style={{ fontSize: '0.875rem' }}>
                        {element.type === 'text' ? 'T' : element.type === 'shape' ? '□' : '🖼️'}
                      </span>
                      <span style={{ fontSize: '0.875rem', color: colors.text.dark }}>
                        {element.type === 'text' ? element.content.substring(0, 15) : element.type}
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: spacing.xs }}>
                      {element.locked && <span>🔒</span>}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          updateElement(element.id, { visible: !element.visible });
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer'
                        }}
                      >
                        {element.visible ? '👁️' : '👁️‍🗨️'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default CreativeEditor;